import { hideVisually, mix, transparentize } from 'polished';
import styled, { css } from 'styled-components';

import { defaultThemeProp } from '../../theme';
import { StyledEmptyState } from '../EmptyState';
import Icon, { StyledIcon } from '../Icon';
import { StyledGrid } from '../Grid';
import { tryCatch } from '../../utils';
import { StyledText } from '../Text';
import { StyledVisuallyHiddenText } from '../VisuallyHiddenText/VisuallyHiddenText';
import { StyledMetaList, StyledMetaListItem } from '../MetaList/MetaList';
import Button, { StyledButton } from '../Button';
import { readableColor } from '../../styles';
import {
  StyledPrimary,
  StyledSecondary,
  StyledSummaryItem,
  StyledSummaryItemActions,
  StyledVisual
} from '../SummaryItem';
import { ellipsisOverflow } from '../../styles/mixins';
import { StyledFlex } from '../Flex';
import { StyledFormControl } from '../FormControl';

import type { MenuProps } from './Menu.types';

export const StyledExpandButton = styled(Button)`
  align-self: center;
`;

export const StyledCheckMarkIcon = styled(Icon)``;

export const StyledMenuItem = styled.li<{
  isParentItem: boolean;
  mode?: MenuProps['mode'];
  itemLayout?: MenuProps['itemLayout'];
  isChecked?: boolean;
  selectableParent?: boolean;
}>(
  ({
    theme: { base, components },
    isParentItem,
    itemLayout,
    mode = 'action',
    isChecked = false,
    selectableParent = false
  }) => {
    const activeColor = tryCatch(() =>
      mix(0.85, base.palette['primary-background'], base.palette.interactive)
    );
    const hoverColor = tryCatch(() =>
      mix(0.95, base.palette['primary-background'], base.palette.interactive)
    );

    const checkedBackground = components['radio-check'][':checked']['background-color'];
    const checkedForeground = tryCatch(() => readableColor(checkedBackground));

    return css`
      min-height: ${base['hit-area']['mouse-min']};
      padding-block: calc(0.75 * ${base.spacing});
      padding-inline: ${base.spacing};
      cursor: pointer;

      > ${StyledSummaryItem} {
        ${StyledSummaryItemActions}, ${StyledVisual} {
          display: grid;
          align-items: center;
        }

        > ${StyledVisual}:has(> :nth-child(2)) {
          grid-template-columns: auto auto;
          grid-column-gap: ${base.spacing};
        }

        ${itemLayout === 'stacked' &&
        css`
          &:has(${StyledSecondary})
            > ${StyledVisual},
            &:has(${StyledSecondary})
            > ${StyledSummaryItemActions} {
            height: ${base['hit-area']['mouse-min']};
            align-self: start;
          }
        `}
      }

      ${itemLayout === 'inline' &&
      css`
        justify-content: flex-start;

        ${StyledSummaryItem} {
          /* Sets grid columns based on SummaryItem content combinations */

          &:has(${StyledVisual}):has(${StyledSecondary}):has(${StyledSummaryItemActions}) {
            grid-template-areas: 'visual primary secondary actions';
            grid-template-columns: auto auto 1fr auto;
          }

          &:has(${StyledVisual}):has(${StyledSecondary}):not(:has(${StyledSummaryItemActions})) {
            grid-template-areas: 'visual primary secondary';
            grid-template-columns: auto auto 1fr;
          }

          &:has(${StyledVisual}):not(:has(${StyledSecondary})):has(${StyledSummaryItemActions}) {
            grid-template-areas: 'visual primary actions';
            grid-template-columns: auto 1fr auto;
          }

          &:has(${StyledVisual}):not(:has(${StyledSecondary})):not(
              :has(${StyledSummaryItemActions})
            ) {
            grid-template-areas: 'visual primary';
            grid-template-columns: auto 1fr;
          }

          &:not(:has(${StyledVisual})):has(${StyledSecondary}):has(${StyledSummaryItemActions}) {
            grid-template-areas: 'primary secondary actions';
            grid-template-columns: auto 1fr auto;
          }

          &:not(:has(${StyledVisual})):has(${StyledSecondary}):not(
              :has(${StyledSummaryItemActions})
            ) {
            grid-template-areas: 'primary secondary';
            grid-template-columns: auto 1fr;
          }

          &:not(:has(${StyledVisual})):not(:has(${StyledSecondary})):has(
              ${StyledSummaryItemActions}
            ) {
            grid-template-areas: 'primary actions';
            grid-template-columns: auto 1fr;
          }

          &:not(:has(${StyledVisual})):not(:has(${StyledSecondary})):not(
              :has(${StyledSummaryItemActions})
            ) {
            grid-template-areas: 'primary';
            grid-template-columns: 1fr;
            flex-grow: 0;
          }

          & > ${StyledPrimary} {
            ${ellipsisOverflow};
          }

          & > ${StyledSecondary} {
            ${ellipsisOverflow};

            ul {
              li {
                ${ellipsisOverflow};
              }
              justify-content: end;
            }
          }

          & > ${StyledSummaryItemActions} > ${StyledFlex} {
            justify-content: end;
          }
        }
      `}

      @media (pointer: coarse) {
        min-height: ${base['hit-area']['finger-min']};
        ${itemLayout === 'stacked' &&
        css`
          &:has(${StyledSecondary})
            > ${StyledVisual},
            &:has(${StyledSecondary})
            > ${StyledSummaryItemActions} {
            height: ${base['hit-area']['finger-min']};
          }
        `}
      }

      &:focus-within {
        background-color: ${activeColor};
      }

      &:hover {
        ${StyledCheckMarkIcon} {
          visibility: visible;
          ${mode === 'single-select' &&
          css`
            ${isChecked
              ? css`
                  color: ${checkedBackground};
                `
              : css`
                  visibility: hidden;
                `}
          `}
          ${mode === 'multi-select' &&
          css`
            ${!isChecked &&
            css`
              border-color: ${components['form-control'][':hover']['border-color']};
            `}
          `}
        }
      }

      &:hover:not([aria-disabled='true']) {
        background-color: ${hoverColor};
      }

      a:first-of-type {
        display: block;
        width: 100%;
        text-decoration: none;
        color: inherit;
      }

      /* The enclosing menu item has required min-height already set */
      ${isParentItem &&
      selectableParent &&
      css`
        ${StyledSummaryItemActions} ${StyledButton} {
          min-height: unset;
        }
      `}

      ${isParentItem &&
      !selectableParent &&
      (mode === 'multi-select' || mode === 'single-select') &&
      css`
        padding-inline-start: calc(1.125rem + ${base.spacing});
      `}

      ${StyledGrid} {
        flex-grow: 1;
      }

      ${StyledCheckMarkIcon} {
        margin-inline-start: 0;
        ${mode === 'single-select' &&
        css`
          ${isChecked
            ? css`
                color: ${checkedBackground};
              `
            : css`
                visibility: hidden;
              `}
        `}
        ${mode === 'multi-select' &&
        css`
          ${isChecked
            ? css`
                color: ${checkedForeground};
                background-color: ${checkedBackground};
                border: 0.0625rem solid ${components['radio-check'][':checked']['border-color']};
              `
            : css`
                color: transparent;
                border: 0.0625rem solid ${components['radio-check']['border-color']};
              `}
          border-radius: min(
            calc(${base['border-radius']} * ${components.checkbox['border-radius']}),
            0.25rem
          );
        `}
      }

      &[aria-disabled='true'] {
        cursor: not-allowed;
        background-color: ${components['form-control'][':disabled']['background-color']};
        opacity: ${base['disabled-opacity']};
      }
    `;
  }
);

StyledMenuItem.defaultProps = defaultThemeProp;

export const StyledAncestors = styled.div`
  ${StyledIcon} {
    width: 1em;
    height: 1em;
  }

  & > ${StyledText} {
    ${ellipsisOverflow};
  }
`;

export const StyledVisibilityHidden = styled.p`
  ${hideVisually}
`;

export const StyledSeparator = styled.li(({ theme }) => {
  return css`
    height: 0.0625rem;
    background-color: ${theme.base.palette['border-line']};
    margin: ${theme.base.spacing} 0;
  `;
});

StyledSeparator.defaultProps = defaultThemeProp;

export const StyledMenuGroupHeader = styled.div(({ theme }) => {
  return css`
    min-height: ${theme.base['hit-area']['mouse-min']};
    font-weight: ${theme.base['font-weight']['semi-bold']};
    background-color: ${theme.base.palette['secondary-background']};

    @media (pointer: coarse) {
      min-height: ${theme.base['hit-area']['finger-min']};
    }
  `;
});

StyledMenuGroupHeader.defaultProps = defaultThemeProp;

export const StyledMenuListHeader = styled.legend(({ theme: { base } }) => {
  const hoverColor = tryCatch(() =>
    mix(0.95, base.palette['primary-background'], base.palette.interactive)
  );
  const activeColor = tryCatch(() =>
    mix(0.85, base.palette['primary-background'], base.palette.interactive)
  );

  return css`
    cursor: pointer;
    width: 100%;
    background-color: ${base.palette['primary-background']};

    &:first-child {
      border-top-left-radius: inherit;
      border-top-right-radius: inherit;
    }

    &:last-child {
      border-bottom-left-radius: inherit;
      border-bottom-right-radius: inherit;
    }

    &:focus-within {
      box-shadow: ${base.shadow['focus-inset']};
      background-color: ${activeColor};
    }

    &:hover:not([aria-disabled='true']):not([data-current='true']) {
      background-color: ${hoverColor};
    }
    color: ${base.palette['foreground-color']};
    padding: calc(0.5 * ${base.spacing}) ${base.spacing};
    text-align: start;
    border-radius: inherit;

    > ${StyledGrid} {
      grid-column-gap: ${base.spacing};
    }

    ${StyledIcon} {
      /* Fixes vertical align issue increasing box size beyond square */
      display: block;
    }
  `;
});

StyledMenuListHeader.defaultProps = defaultThemeProp;

export const StyledMenuList = styled.ul(
  ({ theme }) => css`
    overflow-x: hidden;
    overflow-y: auto;
    list-style: none;
    height: 100%;
    border-radius: inherit;

    ${StyledEmptyState} {
      padding: ${theme.base.spacing};
      height: auto;
    }

    li:not(:first-child):not([role='presentation']) > ${StyledMenuGroupHeader} {
      margin-block-start: ${theme.base.spacing};
    }
  `
);

StyledMenuList.defaultProps = defaultThemeProp;

export const StyledMenuListContainer = styled.fieldset(({ theme }) => {
  return css`
    background-color: ${theme.base.palette['primary-background']};
    border: 0;
    border-radius: inherit;
  `;
});

StyledMenuListContainer.defaultProps = defaultThemeProp;

export const StyledFlyoutMenuListContainer = styled(StyledMenuListContainer)`
  min-width: 10rem;
`;

export const StyledLoadingItem = styled.li`
  display: block;
  position: relative;
  height: 2.8rem;
`;

export const StyledMenuListWrapper = styled.div(
  ({ theme }) => css`
    position: relative;
    overflow: hidden;
    transition: height ${theme.base.animation.speed} ${theme.base.animation.timing.ease};

    & > fieldset:first-child {
      position: relative;
    }

    &:first-child {
      border-top-left-radius: inherit;
      border-top-right-radius: inherit;
    }

    &:last-child {
      border-bottom-left-radius: inherit;
      border-bottom-right-radius: inherit;
    }

    ${StyledMetaList} ${StyledMetaListItem} * {
      display: inline;
      vertical-align: baseline;
      margin: 0;
    }
  `
);

StyledMenuListWrapper.defaultProps = defaultThemeProp;

export const StyledMenu = styled.div(({ theme }) => {
  const borderStyle = `0.0625rem solid ${theme.base.palette['border-line']}`;

  return css`
    display: flex;
    flex-direction: column;

    &,
    ${StyledMenuListWrapper} {
      max-height: inherit;
    }

    &:focus {
      box-shadow: ${theme.base.shadow.focus};
      outline: none;
    }

    &:first-child {
      border-top-left-radius: inherit;
      border-top-right-radius: inherit;
    }

    &:last-child {
      border-bottom-left-radius: inherit;
      border-bottom-right-radius: inherit;
    }

    &[data-active-scope='true'] [data-current='true'] {
      box-shadow: ${theme.base.shadow['focus-inset']};

      @media (forced-colors: active) {
        outline: 0.125rem solid Highlight;
        outline-offset: -0.125rem;
      }

      &:first-child {
        border-start-start-radius: inherit;
        border-start-end-radius: inherit;
      }

      &:last-child {
        border-end-start-radius: inherit;
        border-end-end-radius: inherit;
      }
    }

    ${StyledVisuallyHiddenText}:first-child {
      + ${StyledMenuListWrapper}, + header {
        border-top-left-radius: inherit;
        border-top-right-radius: inherit;
      }
    }

    > header,
    > footer {
      padding: ${theme.base.spacing};
    }

    > header {
      border-bottom: ${borderStyle};
    }

    > footer {
      border-top: ${borderStyle};
    }
  `;
});

StyledMenu.defaultProps = defaultThemeProp;

export const StyledMenuFilter = styled.div(({ theme }) => {
  const borderRadius = `calc(${theme.components['search-input']['border-radius']} * ${theme.base['border-radius']})`;

  return css`
    position: relative;
    min-height: ${theme.components.input.height};
    height: ${theme.components.input.height};
    --icon-start-padding: calc(1.5 * ${theme.components.input.padding});
    /* stylelint-disable-next-line unit-allowed-list */
    --icon-size: max(1.125rem, 14px);

    @media (pointer: coarse) {
      min-height: ${theme.base['hit-area']['finger-min']};
      min-width: ${theme.base['hit-area']['finger-min']};
    }

    ${StyledFormControl} {
      height: 100%;
      border-radius: ${borderRadius};
      padding-inline-start: calc(
        var(--icon-start-padding) + var(--icon-size) + (0.5 * ${theme.components.input.padding})
      );
      padding-inline-end: calc(1.5 * ${theme.components.input.padding});

      &::-webkit-search-cancel-button {
        display: none;
      }

      ::-webkit-search-decoration {
        -webkit-appearance: none;
      }

      &:focus:not([disabled]) {
        box-shadow: ${theme.base.shadow['focus-inset']};
        border-color: ${theme.components['form-control']['border-color']};

        :hover {
          border-color: ${theme.components['form-control'][':hover']['border-color']};
        }
      }
    }
  `;
});

StyledMenuFilter.defaultProps = defaultThemeProp;

export const StyledFilterIcon = styled(Icon)(({ theme }) => {
  const iconColor = tryCatch(() => transparentize(0.3, theme.base.palette['foreground-color']));

  return css`
    position: absolute;
    pointer-events: none;
    color: ${iconColor};
    inset-inline-start: var(--icon-start-padding);
    /* stylelint-disable unit-allowed-list */
    inset-block-start: calc(0.5 * (${theme.components.input.height} - max(1.125rem, 14px)));

    @media (pointer: coarse) {
      inset-block-start: calc(
        0.5 * (${theme.base['hit-area']['finger-min']} - max(1.125rem, 14px))
      );
    }
    /* stylelint-enable unit-allowed-list */
  `;
});

StyledFilterIcon.defaultProps = defaultThemeProp;
